import { multiply, pipe, replace } from "ramda";
import { createStyledBreakpointsTheme } from "styled-breakpoints";

export const BASE_FONT = 16;

export const pxToRem = (px: number): string => `${px / BASE_FONT}rem`;
export const remToPx = pipe(replace("rem", ""), parseInt, multiply(BASE_FONT));

export const breakpointsAscending = ["xs", "sm", "md", "lg", "xl"] as const;
export type BreakpointName = (typeof breakpointsAscending)[number];

const breakpoints = {
  xs: "0px",
  sm: "576px",
  md: "769px",
  lg: "992px",
  xl: "1200px",
} as const satisfies Record<BreakpointName, `${number}px`>;

const grey = {
  900: "#222222",
  800: "#444444",
  700: "#636363",
  600: "#757575",
  500: "#8C8C8C",
  400: "#BFBFBF",
  350: "#D1D1D1",
  300: "#E2E2E2",
  200: "#EFEFEF",
  100: "#F5F5F5",
  75: "#F8F8F8",
  50: "#FAFAFA",
};

const blue = {
  900: "#1A237E",
  800: "#283493",
  700: "#303E9F",
  600: "#3948AB",
  500: "#3F50B5",
  400: "#5C6AC0",
  300: "#7986CB",
  200: "#9FA8DA",
  100: "#C5CAE9",
  50: "#E8EAF6",
};

const red = {
  700: "#CF2A2A",
  600: "#E13430",
  500: "#F03E30",
  400: "#EB4F4B",
  300: "#E2706F",
  200: "#ED9897",
  100: "#FECCD0",
  50: "#FFEAED",
};

const green = {
  900: "#338959",
  800: "#41AB70",
  700: "#49BD7D",
  600: "#53D18C",
  500: "#5BE298",
  400: "#77E8A9",
  300: "#94EEBB",
  200: "#B7F2D0",
  100: "#D4F6E2",
  50: "#EDFBF3",
};

const orange = {
  50: "#FFE8CC",
  500: "#C24D00",
};

const colours = {
  grey,
  blue,
  red,
  green,
  orange,
  white: "#ffffff",
  black: "#000000",
  primary: blue[900],
  body: grey[800],
  bodyLight: grey[600],
  borders: grey[400],
  bordersLight: grey[300],
  bordersDark: grey[500],
  focus: blue[100],
  links: blue[800],
  error: red[600],
  highlight: "#3FC6FF",
};

const typography = {
  xxxl: {
    fontSize: pxToRem(42),
    lineHeight: 1.2,
  },
  xxl: {
    fontSize: pxToRem(26),
    lineHeight: 1.5,
  },
  xl: {
    fontSize: pxToRem(21),
    lineHeight: 1.5,
  },
  lg: {
    fontSize: pxToRem(18),
    lineHeight: 1.3,
  },
  md: {
    fontSize: pxToRem(BASE_FONT),
    lineHeight: 1.5,
  },
  sm: {
    fontSize: pxToRem(14),
    lineHeight: 1.4,
  },
  xs: {
    fontSize: pxToRem(12),
    lineHeight: 1.3,
  },
};

const theme = Object.freeze({
  typography,
  colours,
  ...createStyledBreakpointsTheme({ breakpoints }),
});
export type Theme = typeof theme;
export default theme;

declare module "styled-components" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface DefaultTheme extends Theme {}
}
